import {ITrackEventName, ITrackEventParams} from '@wix/native-components-infra';
import {CartModel} from '../models/checkout/Cart/Cart.model';

type TrackEventTuple = [ITrackEventName, Record<string, any>];

const enum PaymentMethodType {
  PAYPAL = 'paypal',
}

export enum EventAction {
  AgreeToTerms = 'Agree To Terms',
  SuccessCoupon = 'success_coupon',
  FailedCoupon = 'failed_coupon',
  PlaceOrderFailed = 'place_order_failed',
}

const origin = 'Stores';
const eventCategory = 'Enhanced Ecommerce - Stores';

export const lineItemsForAnalytics = (cart: CartModel, ecomAnalyticsUnitPriceAndCartValue: boolean) =>
  cart.lineItems.map((item) => ({
    id: item.source.catalogReference?.catalogItemId,
    catalogReference: item.source.catalogReference,
    name: item.name,
    category: 'All Products',
    price: ecomAnalyticsUnitPriceAndCartValue ? item.pricing.unitPrice.amount : item.pricing.totalPrice?.amount,
    currency: cart.paymentInfo.currencyCode,
    quantity: item.quantityInfo.quantity,
  }));

const analyticsEventBase = (action: EventAction) => ({
  eventCategory,
  eventLabel: origin,
  action,
});

const cartAnalyticsParams = (cart: CartModel, ecomAnalyticsUnitPriceAndCartValue: boolean) => ({
  currency: cart.paymentInfo.currencyCode!,
  cartId: cart.id,
  contents: lineItemsForAnalytics(cart, ecomAnalyticsUnitPriceAndCartValue),
  ...(ecomAnalyticsUnitPriceAndCartValue ? {value: cart.subtotal.amount} : {}),
});

export const FastFlowAnalyticsEventParams = ['AddPaymentInfo', {option: PaymentMethodType.PAYPAL, origin}] as [
  ITrackEventName,
  ITrackEventParams
];

export const AgreeToTermsAnalyticsEventParams = [
  'CustomEvent',
  analyticsEventBase(EventAction.AgreeToTerms),
] as TrackEventTuple;

export const StartPayment = ['StartPayment' as ITrackEventName, {origin}] as TrackEventTuple;

export const AddPaymentInfo = ({activePaymentMethod}: {activePaymentMethod?: string}) =>
  [
    'AddPaymentInfo' as ITrackEventName,
    {
      origin,
      option: activePaymentMethod,
    },
  ] as TrackEventTuple;

export const trackInitiateCheckout = ({
  cart,
  ecomAnalyticsUnitPriceAndCartValue,
}: {
  cart: CartModel;
  ecomAnalyticsUnitPriceAndCartValue: boolean;
}) => {
  return [
    'InitiateCheckout' as ITrackEventName,
    {
      contents: lineItemsForAnalytics(cart, ecomAnalyticsUnitPriceAndCartValue),
      origin,
      ...(ecomAnalyticsUnitPriceAndCartValue
        ? {value: cart.subtotal.amount, currency: cart.paymentInfo.currencyCode}
        : {}),
    },
  ] as TrackEventTuple;
};

export const trackApplyCouponSuccess = ({
  cart,
  couponCode,
  ecomAnalyticsUnitPriceAndCartValue,
}: {
  cart: CartModel;
  couponCode: string;
  ecomAnalyticsUnitPriceAndCartValue: boolean;
}) => {
  return [
    EventAction.SuccessCoupon as unknown as ITrackEventName,
    {
      origin,
      coupon: couponCode,
      ...analyticsEventBase(EventAction.SuccessCoupon),
      ...cartAnalyticsParams(cart, ecomAnalyticsUnitPriceAndCartValue),
    },
  ] as TrackEventTuple;
};

export const trackApplyCouponFailed = ({
  cart,
  couponCode,
  errorCode,
  ecomAnalyticsUnitPriceAndCartValue,
}: {
  cart: CartModel;
  couponCode: string;
  errorCode: string;
  ecomAnalyticsUnitPriceAndCartValue: boolean;
}) => {
  return [
    EventAction.FailedCoupon as unknown as ITrackEventName,
    {
      origin,
      coupon: couponCode,
      ...analyticsEventBase(EventAction.FailedCoupon),
      ...cartAnalyticsParams(cart, ecomAnalyticsUnitPriceAndCartValue),
      error_code: errorCode,
    },
  ] as TrackEventTuple;
};

export const trackPlaceOrderFailedFromCheckout = ({
  errorCode,
  cart,
  ecomAnalyticsUnitPriceAndCartValue,
}: {
  errorCode: string;
  cart: CartModel;
  ecomAnalyticsUnitPriceAndCartValue: boolean;
}): TrackEventTuple => {
  return [
    EventAction.PlaceOrderFailed as unknown as ITrackEventName,
    {
      origin,
      ...analyticsEventBase(EventAction.PlaceOrderFailed),
      ...cartAnalyticsParams(cart, ecomAnalyticsUnitPriceAndCartValue),
      failure_source: 'checkout' as const,
      error_code: errorCode,
    },
  ] as TrackEventTuple;
};

export const trackPlaceOrderFailedFromPayment = ({
  paymentStatus,
  failureDetails,
  cart,
  ecomAnalyticsUnitPriceAndCartValue,
}: {
  paymentStatus: string;
  failureDetails?: string;
  cart: CartModel;
  ecomAnalyticsUnitPriceAndCartValue: boolean;
}): TrackEventTuple => {
  return [
    EventAction.PlaceOrderFailed as unknown as ITrackEventName,
    {
      origin,
      ...analyticsEventBase(EventAction.PlaceOrderFailed),
      ...cartAnalyticsParams(cart, ecomAnalyticsUnitPriceAndCartValue),
      failure_source: 'payment' as const,
      payment_status: paymentStatus,
      failure_details: failureDetails,
    },
  ] as TrackEventTuple;
};
